import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker from './EmojiPicker';
import './MessageActions.css';

function MessageActions({ message, currentUserId, onEdit, onDelete, onReply, onAddReaction, onBookmark, onRemind }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const menuRef = useRef(null);
  const actionsRef = useRef(null);
  const emojiButtonRef = useRef(null);

  // Use message.is_starred from polling updates
  const isBookmarked = message.is_starred || false;
  const isOwnMessage = message.user === currentUserId;

  // Check if actions toolbar should be positioned above instead of below
  useEffect(() => {
    const checkPosition = () => {
      if (actionsRef.current) {
        const rect = actionsRef.current.getBoundingClientRect();
        const parentContainer = actionsRef.current.closest('.message-list');
        if (parentContainer) {
          const containerRect = parentContainer.getBoundingClientRect();
          // If the action bar is within 80px of the container bottom, position it above
          const distanceFromBottom = containerRect.bottom - rect.bottom;
          setPositionAbove(distanceFromBottom < 80);
        }
      }
    };

    // Check position on mount and when scrolling
    checkPosition();
    const messageList = actionsRef.current?.closest('.message-list');
    if (messageList) {
      messageList.addEventListener('scroll', checkPosition);
      return () => messageList.removeEventListener('scroll', checkPosition);
    }
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);
  
  const handleEdit = () => {
    setShowMenu(false);
    onEdit(message);
  };
  
  const handleDelete = () => {
    setShowMenu(false);
    onDelete(message);
  };
  
  const handleReply = () => {
    setShowMenu(false);
    onReply(message);
  };

  const handleEmojiSelect = (emoji) => {
    onAddReaction(message, emoji);
    setShowEmojiPicker(false);
  };

  const handleBookmark = () => {
    setShowMenu(false);
    if (onBookmark) {
      onBookmark(message, !isBookmarked);
      // The isBookmarked state will be updated by polling in useSlackData
    }
  };

  const handleRemind = () => {
    setShowMenu(false);
    if (onRemind) {
      onRemind(message);
    }
  };

  return (
    <div className={`message-actions ${positionAbove ? 'position-above' : ''}`} ref={actionsRef}>
      <button
        ref={emojiButtonRef}
        className="message-action-btn emoji-btn"
        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
        aria-label="Add reaction"
        title="Add reaction"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8s2.91 6.5 6.5 6.5 6.5-2.91 6.5-6.5S11.59 1.5 8 1.5zm0 11.75c-2.9 0-5.25-2.35-5.25-5.25S5.1 2.75 8 2.75 13.25 5.1 13.25 8 10.9 13.25 8 13.25z"/>
          <path d="M5.5 7.25a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 7.25a.75.75 0 100-1.5.75.75 0 000 1.5zM8 11c1.24 0 2.31-.71 2.83-1.75H5.17C5.69 10.29 6.76 11 8 11z"/>
        </svg>
      </button>

      <button
        className="message-action-btn reply-btn"
        onClick={handleReply}
        aria-label="Reply in thread"
        title="Reply in thread"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M3.5 7.5L7 4v2.5c4.5 0 7.5 1.5 9 5.5-1.5-3-4.5-4-9-4V10.5L3.5 7.5z"/>
        </svg>
      </button>

      <button
        className="message-action-btn bookmark-btn"
        onClick={handleBookmark}
        aria-label={isBookmarked ? 'Remove bookmark' : 'Save for later'}
        title={isBookmarked ? 'Remove bookmark' : 'Save for later'}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          {isBookmarked ? (
            <path d="M3 2h10v12l-5-3-5 3V2z" fill="currentColor"/>
          ) : (
            <path d="M3 2v12l5-3 5 3V2H3zm1 1h8v9l-4-2.4L4 12V3z" fill="currentColor"/>
          )}
        </svg>
      </button>

      <button
        className="message-action-btn more-btn"
        onClick={() => setShowMenu(!showMenu)}
        aria-label="More actions"
        title="More actions"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="3" cy="8" r="1.5" fill="currentColor"/>
          <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
          <circle cx="13" cy="8" r="1.5" fill="currentColor"/>
        </svg>
      </button>

      {showMenu && (
        <div className="message-actions-menu" ref={menuRef}>
          <button onClick={handleRemind} className="action-item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 14c.5 0 1-.5 1-1H7c0 .5.5 1 1 1zm4-4V7c0-2-1-3.5-3-4V2.5c0-.5-.5-1-1-1s-1 .5-1 1V3c-2 .5-3 2-3 4v3l-1 1v1h10v-1l-1-1z" fill="currentColor"/>
            </svg>
            Remind me about this
          </button>

          {isOwnMessage && (
            <>
              <button onClick={handleEdit} className="action-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5zm1.5-1.5L14.5 2.5 13 4 11 2l1.5-1.5z" fill="currentColor"/>
                </svg>
                Edit message
              </button>

              <button onClick={handleDelete} className="action-item action-delete">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 2v2H3v2h10V4h-3V2H6zm1 4v6h1V6H7zm3 0v6h1V6h-1zm-6 8h7V7H4v7z" fill="currentColor"/>
                </svg>
                Delete message
              </button>
            </>
          )}
        </div>
      )}

      <EmojiPicker
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onEmojiSelect={handleEmojiSelect}
        referenceElement={emojiButtonRef.current}
      />
    </div>
  );
}

export default MessageActions;